'use client';

import cn from 'classnames';
import Image from 'next/image';
import { useRouter } from 'next/navigation';

import Button from '@/components/common/Button/Button';
import Text from '@/components/common/Text/Text';
import { useModal } from '@/components/hook/modal';
import { useLoggedInUser } from '@/components/hook/user';
import {
  PriveeInterestStatus,
  useExpressPriveeInterest
} from '@/components/hook/priveeInterest';
import {
  useIsPriveeWaitlistEligible,
  useOpenWaitlistModal
} from '@/components/hook/priveeWaitlist';
import { isPriveeInvited, isPriveePending } from '@/util/user_client';
import { FallbackSubscriptionPrices } from '@/types/Subscription';
import { useFlags } from '@/components/hook/flags';
import { FLAG_KEYS } from '@/const/flags';
import AcceptInvitationModal from '@/app/reserve/AcceptInvitationModal/PriveeAcceptInvitationModal';
import PriveeInterestedModal from '@/app/reserve/InterestedModal/PriveeInterestedModal';
import PriveeLoggedOutExpressInterestModal from '@/app/reserve/LoggedOutExpressInterestModal/PriveeLoggedOutExpressInterestModal';

import PriveeWordmarkWhite from 'public/img/logo/reserve-logo-white.svg';

import './PriveeInterestHero.style.scss';

const PRIVEE_VIDEO_MWEB =
  'https://d20cg60zeycvr1.cloudfront.net/videos/privee-video-reveal-1-mweb-compressed.mp4';
const PRIVEE_VIDEO_DESKTOP =
  'https://d20cg60zeycvr1.cloudfront.net/videos/privee-video-reveal-1-rectangle-desktop-compressed.mp4';

type PriveeInterestHeroProps = {
  className?: string;
  /**
   * Where to send the user after they acknowledge the
   * "thank you for your interest" modal. Defaults to `/`.
   */
  thankYouRedirectPath?: string;
};

type PriveePrimaryCtaProps = {
  isInvited: boolean;
  isPending: boolean;
  isWaitlistEligible: boolean;
  isAlreadyInterested: boolean;
  status: PriveeInterestStatus;
  onClick: () => void;
};

function PriveePrimaryCta(props: PriveePrimaryCtaProps) {
  const {
    isInvited,
    isPending,
    isWaitlistEligible,
    isAlreadyInterested,
    status,
    onClick
  } = props;

  if (isInvited) {
    return (
      <Button
        className='privee-interested-button'
        variant='dark'
        size='large'
        onClick={onClick}
      >
        Accept Invitation
      </Button>
    );
  }

  if (isPending) {
    return (
      <Button
        className='privee-interested-button'
        disabled
        variant='light'
        size='large'
      >
        Your R&eacute;serv&eacute; acceptance is pending
      </Button>
    );
  }

  if (isWaitlistEligible) {
    return (
      <Button
        className='privee-interested-button'
        variant='light'
        size='large'
        onClick={onClick}
      >
        Join Waitlist
        <br className='privee-button-newline-on-mobile' /> to Upgrade
      </Button>
    );
  }

  return (
    <>
      <Button
        className={cn('privee-interested-button', {
          'is-submitting': status === 'submitting'
        })}
        disabled={
          isAlreadyInterested || status === 'submitting' || status === 'success'
        }
        variant='light'
        size='large'
        onClick={onClick}
      >
        {status === 'submitting' ? 'Saving…' : "I'm Interested"}
      </Button>

      {status === 'error' && (
        <Text
          variant='text-s'
          className='privee-interested-error'
        >
          Sorry, something went wrong. Please refresh and try again.
        </Text>
      )}

      {isAlreadyInterested && (
        <Text
          variant='text-s'
          className='privee-already-interested'
        >
          Thank you for your interest in R&eacute;serv&eacute;.
          <br />
          We&apos;ll be in touch if you&apos;re selected for an invitation.
        </Text>
      )}
    </>
  );
}

function PriveeInterestHero(props: PriveeInterestHeroProps) {
  const { className = '', thankYouRedirectPath = '/' } = props;
  const { status, isAlreadyInterested } = useExpressPriveeInterest();
  const router = useRouter();
  const user = useLoggedInUser();
  const { showModal, closeModal } = useModal();
  const openWaitlistModal = useOpenWaitlistModal();
  const { value: flagValues } = useFlags();

  const isInvited = isPriveeInvited(user);
  const isPending = isPriveePending(user);
  const isWaitlistEligible = useIsPriveeWaitlistEligible();

  const priveePriceStr =
    FallbackSubscriptionPrices.Privee.asFormattedString.slice(0, -3);

  const shouldShowPriveeInitiationFee =
    flagValues[FLAG_KEYS.PRIVEE_CHARGE_INITIATION_FEE];
  const initiationFeeAsStr = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })
    .format(Number(FallbackSubscriptionPrices.PriveeInitiationFee))
    .slice(0, -3);

  const handlePrimaryClick = async () => {
    if (isInvited) {
      showModal(
        <AcceptInvitationModal
          priveePriceStr={priveePriceStr}
          priveeInitFeeStr={
            shouldShowPriveeInitiationFee ? initiationFeeAsStr : null
          }
          onClose={() => closeModal()}
        />
      );
      return;
    }

    if (isWaitlistEligible) {
      openWaitlistModal(priveePriceStr);
      return;
    }

    if (!user) {
      showModal(<PriveeLoggedOutExpressInterestModal onClose={closeModal} />);
      return;
    }

    showModal(
      <PriveeInterestedModal
        onClose={() => {
          router.push(thankYouRedirectPath);
          closeModal();
        }}
      />
    );
  };

  const handleSeeCollectionClick = () => {
    const banner = document.querySelector('.privee-hero-and-video-container');
    if (!banner) return;
    const top = banner.getBoundingClientRect().bottom + window.scrollY;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <div className={cn('privee-hero-and-video-container', className)}>
      <div className='privee-hero-container'>
        <h1 className='privee-introducing'>
          Introducing
          <Image
            className='privee-wordmark-svg'
            src={PriveeWordmarkWhite}
            height={48}
            alt='Réservé wordmark in white'
          />
        </h1>
        <Text
          variant='text-s'
          className='privee-description'
        >
          Réservé is an invite-only add-on tier within Vivrelle that gives you
          access to an exclusive collection of ultra high-end luxury
          accessories, including{' '}
          <span className='PriveeLoggedOut__introducing-description--bold'>
            Hermès Birkin and Kelly bags, as well as premium styles from Van
            Cleef & Arpels, the Bvlgari Serpenti collection, and more.
          </span>{' '}
          It sits on top of your existing Vivrelle membership and includes a
          separate Réservé closet. Members can borrow one Réservé item at a time
          with a 30-day swap cycle, supported by a dedicated VIP concierge.
        </Text>
        <Text
          variant='text-s'
          className='privee-description'
        >
          <span className='PriveeLoggedOut__introducing-description--bold'>
            Réservé membership begins at $800/month,
          </span>{' '}
          designed for members seeking rare access, white-glove service, and a
          truly elevated experience.{' '}
          <span className='PriveeLoggedOut__introducing-description--bold'>
            R&eacute;serv&eacute; members also have the opportunity to purchase
            items from the R&eacute;serv&eacute; collection at preferred
            members-only pricing, with a portion of their membership fees
            applied towards their purchase.
          </span>
        </Text>

        <PriveePrimaryCta
          isInvited={isInvited}
          isPending={isPending}
          isWaitlistEligible={isWaitlistEligible}
          isAlreadyInterested={isAlreadyInterested}
          status={status}
          onClick={handlePrimaryClick}
        />

        {!isInvited && (
          <Button
            className='privee-see-collection-button'
            variant='dark'
            size='large'
            onClick={handleSeeCollectionClick}
          >
            See the Réservé Collection
          </Button>
        )}
      </div>
      <div className='privee-video-container'>
        <video
          className='privee-video'
          autoPlay
          loop
          muted
          playsInline
          preload='metadata'
        >
          <source
            src={PRIVEE_VIDEO_DESKTOP}
            media='(min-width: 870px)'
          />
          <source src={PRIVEE_VIDEO_MWEB} />
        </video>
      </div>
    </div>
  );
}

export default PriveeInterestHero;
